/**
 * Typed wrappers for the kid-side portfolio + project star toggle.
 *
 * The portfolio endpoint (`/projects/me/portfolio`) returns all
 * starred projects newest-first. Star/unstar is a PUT/DELETE on
 * `/projects/{id}/star`.
 */
import { apiBaseUrl, AuthApiError } from '../account/authApi';

export interface ServerProject {
  id: string;
  name: string;
  kind: 'image' | 'audio' | 'pose';
  block_program: Record<string, unknown> | null;
  from_assignment_id: string | null;
  starred_at: string | null;
  created_at: string;
  updated_at: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${apiBaseUrl()}${path}`;
  const headers = new Headers(init.headers ?? {});
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (response.status === 204) return undefined as T;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // empty
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object' && 'detail' in body && typeof body.detail === 'string'
        ? body.detail
        : null;
    throw new AuthApiError(
      response.status,
      detail,
      `Request to ${path} failed (${response.status}).`,
    );
  }
  return body as T;
}

export async function getMyPortfolio(): Promise<ServerProject[]> {
  return request<ServerProject[]>('/projects/me/portfolio');
}

export async function listMyServerProjects(): Promise<ServerProject[]> {
  return request<ServerProject[]>('/projects');
}

export async function starProject(id: string): Promise<ServerProject> {
  return request<ServerProject>(`/projects/${encodeURIComponent(id)}/star`, { method: 'PUT' });
}

export async function unstarProject(id: string): Promise<ServerProject> {
  return request<ServerProject>(`/projects/${encodeURIComponent(id)}/star`, { method: 'DELETE' });
}
